// UploadThumb — компактный квадратный слот для одного файла (image / video)
// с preview, статусом загрузки, кнопкой удаления и tooltip'ом.
//
// Извлечён из GenerateInputBar.tsx, чтобы переиспользовать в UploadsRow
// (которая рендерится и на /generate, и на home, и на /bots/*-виджете).
//
// Props идентичны старым inline-props в GenerateInputBar — drop-in замена.
import type { UploadedFile, UploadedVideo } from '../utils/uploadHelpers'
import Tooltip from '../../../components/Tooltip'
import SideTooltip from '../../../components/SideTooltip'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { openCharacterSave } from '../../../api/sceneSelection'
import { useAttachedStyleCard } from '../../../api/attachedStyleCard'
import { useAuth } from '../../../context/AuthContext'

export interface UploadThumbProps {
	file: UploadedFile | UploadedVideo | null
	isUploading: boolean
	isLoading: boolean
	accept: string
	inputRef: React.RefObject<HTMLInputElement | null>
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRemove: () => void
	dragProps?: object
	icon: React.ReactNode
	label: string
	required?: boolean
	tooltip?: string
	showHint?: boolean
	onHintDismiss?: () => void
	onShowPicker?: (buttonEl: HTMLElement) => void
	/** Метка «это фото персонажа»: кольцо + мини-аватар в углу (шаг «Применение v2»).
	 *  Хост сам решает, чьё фото в слоте (UploadsRow — charSel, тренд — pipeChar).
	 *  blocked=true — персонаж не подходит модели: кольцо красное (решение владельца
	 *  «ставить, но бордер красным»). */
	characterMark?: { avatar: string | null; letter: string; blocked?: boolean }
}

export default function UploadThumb({
	file,
	isUploading,
	isLoading,
	accept,
	inputRef,
	onChange,
	onRemove,
	dragProps,
	icon,
	label,
	required,
	tooltip,
	showHint: _showHint,
	onHintDismiss,
	onShowPicker,
	characterMark,
}: UploadThumbProps) {
	const isVideoSlot = accept.includes('video/')
	const { user } = useAuth()
	// Карточка с hide_character отключает персонаж-функционал ЦЕЛИКОМ (капсула,
	// пункт меню слота — и «Сохранить как персонажа» здесь). Пин — тот же
	// источник карточки, что у обоих баров (GTM и классический).
	const attachedCard = useAttachedStyleCard()
	// «Сохранить как персонажа» (шаг 7): только фото-слоты с ДОГРУЖЕННЫМ url,
	// только для авторизованных; фото, взятое ИЗ персонажа (пикер шага 5/6),
	// не предлагаем сохранять обратно — путь ключа characters/ виден в presigned.
	const canSaveCharacter =
		!isVideoSlot &&
		!!file?.url &&
		!file.url.includes('/characters/') &&
		!!user &&
		!attachedCard?.hide_character

	const btn = (
		<div className="gm-upload-slot" data-slot-type={isVideoSlot ? 'video' : 'image'} {...dragProps}>
			{file ? (
				<div className={`gm-upload-thumb${characterMark ? ' gm-upload-thumb--char' : ''}${characterMark?.blocked ? ' gm-upload-thumb--char-blocked' : ''}`}>
					{file.preview &&
						(isVideoSlot ? (
							<video
								src={file.preview}
								className="gm-upload-thumb__media"
								muted
								playsInline
								preload="metadata"
								onLoadedMetadata={(e) => {
									e.currentTarget.currentTime = 0.001
								}}
							/>
						) : (
							<img src={file.preview} alt={label} className="gm-upload-thumb__media" />
						))}
					{isUploading && !file.url && (
						<div className="gm-upload-thumb__overlay">
							<div className="gw__loading-dots">
								<span />
								<span />
								<span />
							</div>
						</div>
					)}
					{file.url && (
						<div className="gm-upload-thumb__ok" aria-label={label}>
							<Icon id="gen-check" size={10} />
						</div>
					)}
					{/* Фото подогнали под нейросеть (utils/imagePrepare): что именно — в подсказке; на телефоне
					    это уже сказало уведомление при загрузке. Низ-лево — место бейджей тумбы. */}
					{file.note && (
						<span className="gm-upload-thumb__fixed" role="img" title={file.note} aria-label={file.note}>
							<Icon id="gen-aspect-ratio" size={9} />
						</span>
					)}
					{file.preview && (
						<button
							className="gm-upload-thumb__remove"
							onClick={(e) => {
								e.stopPropagation()
								onRemove()
							}}
							aria-label="Удалить"
							disabled={isLoading}
						>
							<Icon id="gen-close" size={14} />
						</button>
					)}
					{/* Чип-аватар на тумбе убран (решение владельца: дубль — капсула
					    в шапке уже показывает «кто»); принадлежность персонажу несёт
					    рамка --char (characterMark). */}
					{canSaveCharacter && (
						<button
							className="gm-upload-thumb__char"
							onClick={(e) => {
								e.stopPropagation()
								openCharacterSave(file!.url!)
							}}
							aria-label="Сделать аватаром"
							title="Сделать аватаром"
							disabled={isLoading}
						>
							<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
								<circle cx="12" cy="8" r="3.4" />
								<path d="M5 20c1.3-3.4 3.9-5 7-5s5.7 1.6 7 5" />
							</svg>
						</button>
					)}
				</div>
			) : isUploading ? (
				<div className="gm-upload-slot__reuse-loading">
					<div className="gw__loading-dots">
						<span />
						<span />
						<span />
					</div>
				</div>
			) : (
				<button
					type="button"
					className={`gm-upload-btn ${required ? 'gm-upload-btn--required' : ''}`}
					onClick={(e) => {
						onHintDismiss?.()
						if (onShowPicker) onShowPicker(e.currentTarget)
						else inputRef.current?.click()
					}}
					disabled={isLoading}
					aria-label={label}
					title={label}
				>
					{icon}
					{required && <span className="gm-upload-btn__dot" aria-hidden="true" />}
				</button>
			)}
			<input
				ref={inputRef}
				type="file"
				accept={accept}
				onChange={onChange}
				disabled={isLoading || isUploading}
				style={{ display: 'none' }}
			/>
		</div>
	)

	if (file?.preview) {
		return isVideoSlot ? (
			<Tooltip videoPreview={file.preview}>{btn}</Tooltip>
		) : (
			<Tooltip imagePreview={file.preview}>{btn}</Tooltip>
		)
	}

	return tooltip ? <SideTooltip text={tooltip}>{btn}</SideTooltip> : btn
}
